import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  BarChartIcon,
  BriefcaseIcon,
  BusinessCardIcon,
  CreditCardIcon,
  HomeIcon,
  LogOutIcon,
  MenuIcon,
  ShieldIcon,
  UserIcon,
  UsersIcon,
} from '../common/Icons';
import QrCodeModal from '../../pages/business/QrCodeModal';
import { getLanguageOption, getNextLanguageCode } from '../../utils/languages';
import AuthenticatedImage from '../common/AuthenticatedImage';

const navItems = [
  { to: '/dashboard', labelKey: 'navigation.home', icon: HomeIcon },
  { to: '/businesses', labelKey: 'navigation.businesses', icon: BriefcaseIcon },
  { to: '/clients', labelKey: 'navigation.contacts', icon: UsersIcon },
  { to: '/visualization', labelKey: 'navigation.reports', icon: BarChartIcon },
  { to: '/subscription', labelKey: 'navigation.payments', icon: CreditCardIcon },
];

const navBase =
  'flex h-12 items-center gap-3 rounded-lg px-4 text-[15px] font-medium transition-colors';
const navInactive = 'text-slate-300 hover:bg-white/5 hover:text-white';
const navActive = 'bg-white/10 text-sky-400';
const BRAND_LOGO = '/logos/logo-alta-branca.png?v=20260520';

const MainLayout = () => {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);

  const currentLanguage = getLanguageOption(i18n.language);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const changeLanguage = () => {
    i18n.changeLanguage(getNextLanguageCode(i18n.language));
  };
  const userAvatar = user?.avatar;
  const showProfileShortcut = location.pathname !== '/profile';

  useEffect(() => {
    setAvatarFailed(false);
  }, [userAvatar]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-950/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-gradient-to-b from-[#111827] to-[#3b4758] text-white transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-28 items-center px-5">
          {!logoFailed && (
            <img
              src={BRAND_LOGO}
              alt="Clarity Files"
              className="h-[84px] w-auto max-w-[168px] object-contain"
              onError={() => setLogoFailed(true)}
            />
          )}
        </div>

        <nav className="mt-2 flex flex-1 flex-col gap-2 px-4" aria-label="Menu principal">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `${navBase} ${isActive ? navActive : navInactive}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              <span>{t(item.labelKey)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-900/30 p-4">
          {user?.role === 'admin' && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `${navBase} mb-2 ${isActive ? navActive : navInactive}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <ShieldIcon className="h-5 w-5 shrink-0" />
              <span>{t('navigation.admin')}</span>
            </NavLink>
          )}
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              `${navBase} ${isActive ? navActive : navInactive}`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <UserIcon className="h-5 w-5 shrink-0" />
            <span className="truncate">{user?.name || t('navigation.profile')}</span>
          </NavLink>
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className={`${navBase} mt-2 ${navInactive}`}
            onClick={() => setSidebarOpen(false)}
          >
            <ShieldIcon className="h-5 w-5 shrink-0" />
            <span>{t('terms.link', 'Termos de uso')}</span>
          </a>
          <button
            className={`${navBase} mt-2 w-full text-slate-300 hover:bg-white/5 hover:text-white`}
            onClick={handleLogout}
          >
            <LogOutIcon className="h-5 w-5 shrink-0" />
            <span>{t('navigation.logout')}</span>
          </button>
        </div>
      </aside>

      <div className="min-h-screen lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/95 px-4 backdrop-blur lg:px-8">
          <button
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-slate-700 lg:hidden"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={t('actions.openMenu')}
          >
            <MenuIcon className="h-5 w-5" />
          </button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-4">
            <button
              className="hidden h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-sm transition hover:border-sky-200 hover:text-sky-600 sm:inline-flex"
              onClick={() => setQrOpen(true)}
              type="button"
            >
              <BusinessCardIcon className="h-4 w-4" />
              {t('actions.generateQrCode')}
            </button>
            <button
              className="inline-flex h-9 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-lg leading-none text-slate-900 shadow-sm transition hover:border-sky-200 hover:text-sky-600 focus:outline-none focus:ring-2 focus:ring-sky-200"
              onClick={changeLanguage}
              type="button"
              aria-label={`${t('actions.changeLanguage')}: ${currentLanguage.accessibilityLabel}`}
              title={currentLanguage.accessibilityLabel}
            >
              <span aria-hidden="true">{currentLanguage.flag}</span>
            </button>
            {showProfileShortcut && (
              <NavLink
                to="/profile"
                className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-sky-50 text-sky-500 ring-1 ring-sky-100 transition hover:bg-sky-100"
                aria-label={t('navigation.profile')}
              >
                {userAvatar && !avatarFailed ? (
                  <AuthenticatedImage
                    source={userAvatar}
                    alt=""
                    className="h-full w-full object-cover"
                    onLoadError={() => setAvatarFailed(true)}
                  />
                ) : (
                  <UserIcon className="h-5 w-5" />
                )}
              </NavLink>
            )}
          </div>
        </header>

        <main className="px-4 py-8 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
      <QrCodeModal isOpen={qrOpen} onClose={() => setQrOpen(false)} />
    </div>
  );
};

export default MainLayout;
